import { css, ThemeProvider, useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useUserSessionContext } from '../../components/UserSessionContextWrapper';
import useMessageService from '../../services/i18n/useMessageService';
import ApplicationGrid from './ApplicationGrid';
import getTargetAttribute from '../../services/getTargetAttribute';
import { isMobile } from '../../services/DeviceInformation';
import HomeTitleMobile from './HomeTitleMobile';
import HomeTitleDesktop from './HomeTitleDesktop';

const theme = {
    linkFontSize: '1rem',
    textInputHeight: '2.3333rem',
    textInputFontSize: '1rem',
    textInputBorderRadius: '2px',
    textInputSpacing: '0',
    textInputSpacingVariant1: '0.6666rem',
};

export default function HomeContent() {
    const { grayLighter } = useTheme();
    const { systemsData } = useUserSessionContext();
    const [[myApplications, searchPlaceholder, noApplicationsTitle, noApplicationsSubtitle]] =
        useMessageService([
            'home.my_applications',
            'general.search',
            'home.no_applications',
            'home.no_applications_subtitle',
        ]);

    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [applications, setApplications] = useState([]);

    useEffect(() => {
        if (systemsData) {
            setLoading(false);
            setApplications(systemsData);
        }
    }, [systemsData]);

    const handleSearch = (term) => {
        setSearchTerm(term);
    };

    const basicTitle = (
        <h1
            css={css`
                margin: 0 0 2rem 0;
                font-size: 2rem;
                font-weight: normal;
            `}
        >
            {myApplications}
        </h1>
    );

    if (loading) {
        return basicTitle;
    }

    if (applications.length === 0) {
        return (
            <>
                <div
                    css={css`
                        position: absolute;
                    `}
                >
                    {basicTitle}
                </div>
                <div
                    css={css`
                        flex-grow: 1;
                        text-align: center;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        align-items: center;
                    `}
                >
                    <h2
                        css={css`
                            font-size: 2rem;
                            margin: 0 0 1.3333rem 0;
                        `}
                        data-target={getTargetAttribute('home-no-applications-title')}
                    >
                        {noApplicationsTitle}
                    </h2>
                    <h3
                        css={css`
                            font-size: 1.25rem;
                            font-weight: normal;
                            color: ${grayLighter};
                            margin: 0;
                        `}
                        data-target={getTargetAttribute('home-no-applications-subtitle')}
                    >
                        {noApplicationsSubtitle}
                    </h3>
                </div>
            </>
        );
    }

    const title = isMobile() ? (
        <HomeTitleMobile
            onChange={(e, value) => {
                handleSearch(value);
            }}
            onClear={() => {
                handleSearch('');
            }}
            onSearch={handleSearch}
            searchPlaceholder={searchPlaceholder}
            searchTerm={searchTerm}
            title={basicTitle}
        />
    ) : (
        <HomeTitleDesktop
            onChange={(e, value) => {
                handleSearch(value);
            }}
            onClear={() => {
                handleSearch('');
            }}
            onSearch={handleSearch}
            searchPlaceholder={searchPlaceholder}
            searchTerm={searchTerm}
            title={basicTitle}
        />
    );

    return (
        <ThemeProvider theme={theme}>
            {title}
            <ApplicationGrid
                loading={loading}
                applications={applications}
                searchTerm={searchTerm}
            />
        </ThemeProvider>
    );
}
